import { css } from '@emotion/react'
import { colors, HFlow, Tag, VFlow } from 'bold-ui'
import { HLabel } from 'components/HLabel'
import { differenceInDays, startOfDay } from 'date-fns'
import { useAcompanhamentoUltimosContatosQuery, useInformacoesPuerperioQuery } from 'graphql/hooks.generated'
import { FonteIdadeGestacionalEnum, Sort, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { GRUPO_CBO_ACOMP_PRE_NATAL_E_PUERPERIO } from 'types/NivelCbo'
import { calculateIdadeGestacional } from 'util/atendimento/gestante/calculateIdadeGestacional'
import { formatDate, toDate } from 'util/date/formatDate'
import { formatIdadeGestacional } from 'view/atendimentos/detail/soap/pre-natal/util-preNatal'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { getFiltrosConsultasPorAcompanhamento } from '../../util/util-acompanhamentos'

interface InformacoesPuerperioCardProps {
  prontuarioId: ID
  cidadaoId: ID
  atendimentoId: ID
  dataInicioPuerperio: Date
  dataFimPuerperio: Date
}

export function InformacoesPuerperioCard(props: Readonly<InformacoesPuerperioCardProps>) {
  const { prontuarioId, cidadaoId, atendimentoId, dataInicioPuerperio, dataFimPuerperio } = props

  const { getServerTimeNow } = useServerTime()
  const now = getServerTimeNow()

  const {
    data: { informacoesPuerperio },
    loading,
  } = useInformacoesPuerperioQuery({
    variables: { prontuarioId },
    skip: !prontuarioId || !dataInicioPuerperio,
    fetchPolicy: 'cache-first',
  })

  const filtroConsultasPuerperio = getFiltrosConsultasPorAcompanhamento(TipoAcompanhamentoEnum.PUERPERIO)

  const { data: consultasPuerperio, loading: loadingConsultas } = useAcompanhamentoUltimosContatosQuery({
    variables: {
      input: {
        cidadaoId,
        somenteMeusRegistros: false,
        sortDirection: Sort.DESC,
        tiposRegistro: filtroConsultasPuerperio.tiposRegistro,
        excludeEscutaOrPreAtendimentoFromAtendProf: atendimentoId,
        pageParams: {
          size: 1,
        },
        filtrosAvancados: {
          periodo: { startDate: dataInicioPuerperio, endDate: dataFimPuerperio },
          cod2002Cbos: [...GRUPO_CBO_ACOMP_PRE_NATAL_E_PUERPERIO],
          exibirRegistrosInativos: false,
          somenteRegistrosComErrataNotaComplementar: false,
          codCiaps2: filtroConsultasPuerperio.codCiaps2,
          codCids10: filtroConsultasPuerperio.codCids10,
        },
      },
    },
    skip: !cidadaoId || !dataInicioPuerperio,
    fetchPolicy: 'cache-first',
  })

  const dataDesfecho = informacoesPuerperio?.preNatal.dataDesfecho
  const diasDePuerperio = differenceInDays(startOfDay(now), startOfDay(dataInicioPuerperio))
  const isFonteIgDum = informacoesPuerperio?.preNatal.fonteIdadeGestacional === FonteIdadeGestacionalEnum.DUM
  const igParto = dataDesfecho
    ? calculateIdadeGestacional(toDate(informacoesPuerperio?.dataInicioGestacao), toDate(dataDesfecho))
    : null
  const dataUltimaConsulta = consultasPuerperio?.historico?.content.first()?.dataAtendimento

  return (
    <AcompanhamentoCard
      id='informacoes-puerperio'
      title='Informações do puerpério'
      tipoAcompanhamento={TipoAcompanhamentoEnum.PUERPERIO}
      loading={loading || loadingConsultas}
      headerContent={
        <Tag
          style={css`
            background: ${colors.blue.c80};
          `}
        >
          {diasDePuerperio} dias
        </Tag>
      }
    >
      <HFlow
        justifyContent='space-between'
        style={css`
          padding: 0.5rem;
        `}
      >
        <VFlow vSpacing={0.5}>
          <HLabel title='Data do desfecho:'>{dataDesfecho && formatDate(dataDesfecho)}</HLabel>
          <HLabel title='Tipo de gravidez:'>{informacoesPuerperio?.preNatal.tipoGravidez?.descricao}</HLabel>
        </VFlow>
        <VFlow vSpacing={0.5}>
          <HLabel title={`IG parto (${isFonteIgDum ? 'DUM' : 'eco. obst.'}):`}>
            {formatIdadeGestacional(igParto?.emDias)}
          </HLabel>
          <HLabel title='Risco da gestação:'>{informacoesPuerperio?.preNatal.altoRisco ? 'Alto' : 'Habitual'}</HLabel>
        </VFlow>
        <VFlow vSpacing={0.5}>
          <HLabel title='Última consulta:'>{dataUltimaConsulta && formatDate(dataUltimaConsulta)}</HLabel>
        </VFlow>
      </HFlow>
    </AcompanhamentoCard>
  )
}
